import React, { useState } from 'react';
import { RxStar, RxStarFilled } from 'react-icons/rx';
import { RiStarHalfLine } from 'react-icons/ri';
import { TbStarOff } from 'react-icons/tb';
import classNames from 'classnames';

export type RatingStarsProps = {
  value: number;
  onChange?: (value: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const sizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

export function RatingStars({ value, onChange, readonly = false, size = 'md', className }: RatingStarsProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const displayValue = hoverValue !== null && !readonly ? hoverValue : value;

  const handleClick = (starValue: number) => {
    if (!readonly && onChange) {
      onChange(starValue);
    }
  };

  const handleMouseEnter = (starValue: number) => {
    if (!readonly) {
      setHoverValue(starValue);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoverValue(null);
    }
  };

  // Déterminer la couleur en fonction de la note
  const getStarColor = (currentValue: number) => {
    if (currentValue < 3) return 'text-error';
    if (currentValue < 4) return 'text-warning';
    return 'text-yellow-500';
  };

  const starColor = getStarColor(displayValue);

  return (
    <div className={classNames('flex items-center gap-0.5', className)}>
      {/* Reset button (dashed star) - only shown when value > 0 */}
      {value > 0 && !readonly && (
        <button
          type="button"
          onClick={() => handleClick(0)}
          className={classNames(sizeClasses[size], 'cursor-pointer hover:scale-110 transition-transform text-gray-400 hover:text-gray-600 mr-1')}
          aria-label="Réinitialiser la note"
          title="Réinitialiser la note à 0"
        >
          <TbStarOff className="w-full h-full" />
        </button>
      )}

      {[1, 2, 3, 4, 5].map((starValue) => {
        const floorValue = Math.floor(displayValue);
        const decimal = displayValue - floorValue;

        const isFilled = starValue <= floorValue;
        const isHalf = starValue === floorValue + 1 && decimal >= 0.3;

        const Icon = isFilled ? RxStarFilled : isHalf ? RiStarHalfLine : RxStar;

        return (
          <button
            key={starValue}
            type="button"
            onClick={() => handleClick(starValue)}
            onMouseEnter={() => handleMouseEnter(starValue)}
            onMouseLeave={handleMouseLeave}
            disabled={readonly}
            className={classNames(sizeClasses[size], {
              'cursor-pointer hover:scale-110 transition-transform': !readonly,
              'cursor-default': readonly,
              [starColor]: isFilled || isHalf,
              'text-gray-300': !isFilled && !isHalf,
            })}
            aria-label={`${starValue} étoile${starValue > 1 ? 's' : ''}`}
          >
            <Icon className="w-full h-full" />
          </button>
        );
      })}
    </div>
  );
}
